import { Overline, TextLink } from "@/components/typography"
import { Skeleton } from "@/components/ui/skeleton"
import { cn } from "cn"
import type { Platform, Talent } from "@/shared/types"

const HANDLE_PLATFORMS: Platform[] = ["TikTok", "Instagram", "YouTube"]

export type CreatorCellProps = {
  talent?: Talent
  className?: string
}

export function CreatorCell({ talent, className }: CreatorCellProps) {
  const handle = creatorHandle(talent)
  const displayName = handle ? `@${handle.replace(/^@/, "")}` : talent?.name
  const country = talent?.country
  const managerEmail = talent?.managers?.[0]?.email
  const imgUrl = talent?.imgUrl || talent?.manualImgUrl

  // Avatar + identity sit in one row. Figma `Pitch - Creator - Row-Size v2`.
  return (
    <div className={cn("flex items-center gap-2", className)}>
      <div
        className="size-12 shrink-0 rounded-md bg-muted bg-cover bg-center bg-no-repeat"
        style={imgUrl ? { backgroundImage: `url(${imgUrl})` } : undefined}
        role="img"
        aria-label={displayName || "Creator"}
      />
      <div className="min-w-0 space-y-0.5">
        {displayName ? (
          <div className="truncate text-lg/none font-bold">{displayName}</div>
        ) : null}
        {country ? <Overline className="font-medium">{country}</Overline> : null}
        {managerEmail ? (
          <div className="truncate text-tiny leading-none">
            <TextLink href={`mailto:${managerEmail}`}>{managerEmail}</TextLink>
          </div>
        ) : null}
      </div>
    </div>
  )
}

export function CreatorCellSkeleton({ className }: { className?: string }) {
  return (
    <div className={cn("flex items-center gap-2", className)}>
      <Skeleton className="size-12 shrink-0 rounded-md" />
      <div className="min-w-0 space-y-0.5">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-3 w-16" />
      </div>
    </div>
  )
}

export function creatorHandle(talent: Talent | undefined) {
  for (const platform of HANDLE_PLATFORMS) {
    const username = talent?.platformData?.[platform]?.username
    if (username) {
      return username
    }
  }
  return undefined
}
